/** @jsx jsx */
import { Avatar, Grid, List, Spinner } from "@cd2/components/build/es6"
import { useSkeem } from "../../../providers/SkeemProvider"
import { H2, H4, P, TextBlock } from "@cd2/text"
import { css, jsx } from "@emotion/core"
import { format } from "date-fns"
import React, { useState } from "react"
import { useAsyncEffect } from "../../../hooks/useAsyncEffect"
import { useMediaBreakpoints } from "../../../hooks/useMediaBreakpoints"
import { Card } from "../../../interfaceParts/Card"
import { HoverFlowResultsLoader } from "../../../interfaceParts/hoverLoaders/HoverFlowResultsLoader"
import { LoadingTable } from "../../../interfaceParts/LoadingTable"
import { ReloadingOverlay } from "../../../interfaceParts/ReloadingOverlay"
import { IRecord } from "../../../types/schema"
import { Frame } from "../../../interfaceParts/Frame"
import { DelayedRender } from "../../../interfaceParts/DelayedRender"
import { Pill } from "../../../interfaceParts/Pill"
import { useTheme } from "@cd2/theme"
import { Flex } from "../../../interfaceParts/Flex"
import { IWebClient } from "@cd2/skeem-client"
import { usePromiseResolver } from "../../../hooks/usePromiseResolver"

type IFlowTag = IRecord<"flowTags", "name"> & { flows: IRecord<"flows", "name">[] }
export type IEmployeeFlowTag = IRecord<
  "employeeFlowTags",
  "flowTag" | "removedOn" | "createdAt" | "active" | "expiresOn"
> & {
  flowTag: IFlowTag
  flowResults: IRecord<"flowResults", "flowName">[]
  date: Date
  added: boolean
  expired?: boolean
}
export function UserManageEmployeesFlowTagsTab({
  employeeId,
  historyOnly,
}: {
  employeeId: string
  historyOnly?: boolean
}) {
  const skeem = useSkeem()
  const { isTablet, isMobile } = useMediaBreakpoints()
  const isSmall = isMobile || isTablet
  const [isLoading, setIsLoading] = useState(true)
  const [tags, setTags] = useState<IEmployeeFlowTag[] | null>(null)

  async function loadTags() {
    if (!employeeId) {
      return
    }
    try {
      setIsLoading(false)
      const tags = await skeem.fetch2<IEmployeeFlowTag[]>({
        model: {
          name: "employeeFlowTags",
          attributes: [
            "flowTag",
            "removedOn",
            "createdAt",
            "active",
            "expiresOn",
            {
              as: "expired",
              value: {
                and: [
                  { not: { matches: { expiresOn: null } } },
                  { matches: { expiresOn: { lte: new Date() } } },
                ],
              },
            },
            { name: "flowResults", attributes: ["flowName"] },
            { name: "flowTag", attributes: ["name", { name: "flows", attributes: ["name"] }] },
          ],
          sort: { by: "createdAt", direction: "desc" },
          filter: { matches: { employee: employeeId } },
        },
      })
      setTags(format(tags))
    } finally {
      setIsLoading(false)
    }
  }

  function format(tags: IEmployeeFlowTag[]) {
    const allTags: IEmployeeFlowTag[] = []
    tags.forEach(tag => {
      if (tag.removedOn) {
        allTags.push({ ...tag, date: tag.removedOn, id: tag.id + "-removed", added: false })
      }
      allTags.push({ ...tag, date: tag.createdAt, added: true })
    })
    return allTags.sort((a, b) => b.date.valueOf() - a.date.valueOf())
  }

  useAsyncEffect(async () => {
    await loadTags()
  }, [employeeId])

  if (!tags) {
    return (
      <Frame minHeight={300}>
        <DelayedRender>
          <Spinner color="black" />
        </DelayedRender>
      </Frame>
    )
  }

  return (
    <div>
      {historyOnly ? (
        <TagHistory tags={tags} isLoading={isLoading} />
      ) : (
        <Grid columns={isSmall ? undefined : [{ fr: 1 }, { fr: 1 }]} gap={16}>
          {isSmall ? (
            <ActiveTags employeeId={employeeId} />
          ) : (
            <TagHistory tags={tags} isLoading={isLoading} />
          )}
          {isSmall ? (
            <TagHistory tags={tags} isLoading={isLoading} />
          ) : (
            <ActiveTags employeeId={employeeId} />
          )}
        </Grid>
      )}
    </div>
  )
}
function TagHistory({ tags, isLoading }: { tags: IEmployeeFlowTag[]; isLoading: boolean }) {
  return (
    <ReloadingOverlay isReloading={isLoading}>
      {!isLoading && tags && tags.length === 0 ? (
        <Card
          css={css`
            padding: 50px 16px;
          `}
          secondary
        >
          <TextBlock size="small" align="center">
            <H2>No results... 📦</H2>
            <P>This employee has no flow tags yet.</P>
          </TextBlock>
        </Card>
      ) : isLoading ? (
        <LoadingTable recordCount={10} />
      ) : (
        <Grid gap={8}>{tags?.map(tag => <FlowTagCard key={tag.id} tag={tag} />)}</Grid>
      )}
    </ReloadingOverlay>
  )
}

export async function fetchActiveTags(
  skeem: IWebClient,
  opts: { employeeIds: string[]; ignoreDuplicates?: boolean },
) {
  console.log("FETCHING", opts)
  type ITag = IRecord<
    "employeeFlowTags",
    "flowTag" | "createdAt" | "active" | "employee" | "expiresOn"
  > & {
    flowTag: IFlowTag
    flowResults: IRecord<"flowResults", "flowName">[]
    added: boolean
  }

  if (opts.employeeIds.length === 0) {
    return []
  }

  const tags = await skeem.fetch2<ITag[]>({
    model: {
      name: "employeeFlowTags",
      attributes: [
        "flowTag",
        "createdAt",
        "active",
        "expiresOn",
        { name: "flowResults", attributes: ["flowName"] },
        { name: "flowTag", attributes: ["name", { name: "flows", attributes: ["name"] }] },
        "employee",
      ],
      sort: { by: "createdAt", direction: "desc" },
      filter: {
        and: [{ matches: { employee: { or: opts.employeeIds } } }, activeEmployeeFlowTagFilter()],
      },
    },
  })

  const latestTagsForEachFlow = new Map<
    string, // employeeId-flowResultId
    { employeeId: string; resultId: string; latest: ITag; history: ITag[] }
  >()

  tags.forEach(tag => {
    tag.flowResults.forEach(flowResult => {
      const key = `${tag.employee!.id}-${flowResult.id}`
      const existing = latestTagsForEachFlow.get(key)
      if (!existing) {
        latestTagsForEachFlow.set(key, {
          employeeId: tag.employee!.id,
          resultId: flowResult.id,
          latest: tag,
          history: [],
        })
      } else if (new Date(existing.latest.createdAt) <= new Date(tag.createdAt)) {
        existing.history.push(existing.latest)
        existing.latest = tag
      } else {
        existing.history.push(tag)
      }
    })
  })

  let activeTags = Array.from(latestTagsForEachFlow.values())

  if (opts.ignoreDuplicates) {
    const seenFlowTagIds = new Set()
    activeTags = activeTags.filter(tag => {
      if (seenFlowTagIds.has(tag.latest.id)) {
        return false
      } else {
        seenFlowTagIds.add(tag.latest.id)
        return true
      }
    })
  }

  return activeTags
}

export function ActiveTags({ employeeId, noTitle }: { employeeId: string; noTitle?: boolean }) {
  const skeem = useSkeem()
  const { themeColor } = useTheme()

  const activeTags = usePromiseResolver(
    () => fetchActiveTags(skeem, { employeeIds: [employeeId] }),
    [employeeId],
  )

  if (!activeTags) {
    return null
  }

  return (
    <div>
      <Grid gap={16}>
        {!noTitle && <H2>Active flow tags</H2>}
        {activeTags.length > 0 ? (
          <React.Fragment>
            {activeTags.map(({ resultId, latest, history }) => (
              <Card key={resultId} border>
                <Flex gap={8} wrap>
                  <Pill color={themeColor("primary")}>{latest.flowTag.name}</Pill>
                  {history.map(tag => (
                    <Pill
                      key={tag.id}
                      css={css`
                        font-weight: normal;
                        opacity: 0.7;
                      `}
                      color={themeColor("lightgrey")}
                    >
                      {tag.flowTag.name}
                    </Pill>
                  ))}
                </Flex>
                <List horizontal allowWrap>
                  <P blockSpacing={0}>Added by case:</P>
                  <HoverFlowResultsLoader id={resultId} />
                </List>
              </Card>
            ))}
          </React.Fragment>
        ) : (
          <P>There are no active flow tags for this employee.</P>
        )}
      </Grid>
    </div>
  )
}

const expireIcon = "history"
const removedIcon = "minus"
const addedIcon = "plus"
const colorGreen = "green"
const colorRed = "red"

function getTagIcon(tag: IEmployeeFlowTag) {
  const expiresOn = tag.expiresOn ? new Date(tag.expiresOn) : null
  const removedOn = tag.removedOn ? new Date(tag.removedOn) : null

  // active tags that expire never get unflagged active, as they are considered inactive already
  // so just need to check if tag is active when doing expired check, otherwise can fairly safely assume
  // that if tag is not active, it is removed
  if (tag.active && tag.expired) {
    return { icon: expireIcon, color: colorRed }
  }

  // If for w/e reason it was removed after expiring, we still want to show it as expired
  if (expiresOn && removedOn && expiresOn.valueOf() < removedOn.valueOf()) {
    return { icon: expireIcon, color: colorRed }
  }
  return tag.added ? { icon: addedIcon, color: colorGreen } : { icon: removedIcon, color: colorRed }
}

function FlowTagCard({ tag }: { tag: IEmployeeFlowTag }) {
  const tagData = getTagIcon(tag)
  return (
    <Grid
      columns={[40, "auto"]}
      gap={8}
      css={css`
        position: relative;
        :not(:last-child):after {
          content: "";
          position: absolute;
          left: 19px;
          top: 45px;
          height: calc(100% - 18px);
          border-left: 3px solid #dfdfdf;
        }
      `}
    >
      <Avatar
        color={tagData.color}
        content={{
          icon: tagData.icon,
        }}
      />
      <div>
        <H4>{tag.flowTag.name}</H4>

        {tag.added ? (
          <P>First added on: {format(new Date(tag.createdAt), "dd/MM/yyyy - p")}</P>
        ) : tag.removedOn ? (
          <P>Removed on: {format(new Date(tag.removedOn), "dd/MM/yyyy - p")}</P>
        ) : null}
        {tag?.expiresOn ? (
          <P>
            {tag.expired ? "Expired on" : "Expires on"}:{" "}
            {format(new Date(tag.expiresOn), "dd/MM/yyyy - p")}
          </P>
        ) : null}
        {tag.flowResults.length > 0 && (
          <List horizontal allowWrap>
            <P blockSpacing={0}>Add by:</P>
            {tag.flowResults.map(f => (
              <HoverFlowResultsLoader key={f.id} id={f.id} />
            ))}
          </List>
        )}
      </div>
    </Grid>
  )
}

export const activeEmployeeFlowTagFilter = () => ({
  or: [
    { matches: { active: true, expiresOn: null } },
    { matches: { active: true, expiresOn: { gte: new Date() } } },
  ],
})
